import { useEffect, useState } from 'react';
import { usePage } from '../../context/PageContext';

import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useParams } from 'react-router-dom';
import { ROUTES, SystemConstants } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import { CalendarOutline, Slash } from '../icons';
import { useFetchTaskDetail } from '../../hooks/useFetchTaskDetail';
import { useTask } from '../../context/TaskContext';
import { ITask, IUpdateTask } from '../../interface/tasks';
import {
  extractDate,
  extractTime,
  getChangedFields,
  mergeDateAndTime,
} from '../../constants/utilityFunctions';
import { showToast } from '../utils/toast';
import { useTaskMutations } from '../../hooks/useTaskMutations';
import { useUser } from '../../context/UserContext';
import { Form, FormItem, Heading, Input, Spin, Switch, Text } from '../Ant';
import TextAreaField from '../textAreaField/textAreaField';
import { Flex } from 'antd';
import dayjs from 'dayjs';
import { TimePicker } from '../Ant/TimePicker';
import { taskFields, taskRelations, TasksPriority } from '../../constants/tasks';
import { useFetchActivityStatus } from '../../hooks/useFetchActivityStatus';
import CustomDatePicker from '../Ant/CustomDatePicker/CustomDatePicker';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsPage } from '../../interface/analytics';
import CustomModal from '../informationPopup/customModal';
import ApiErrorModal from '../informationPopup/apiErrorModal';
import FieldErrorMessage from '../fieldErrorMessage/fieldErrorMessage';
import { useTaskFieldError } from '../../hooks/useTaskFieldError';
import { FormCard } from '../../assets/styles/components/formCard.styled';
import { isTaskFormChanged } from './isTaskFormChanged';
import TaskStatusSelect from '../criticalTask/taskStatusSelect';
import TaskMatterSelect from '../criticalTask/taskMatterSelect';
import TaskPrioritySelect from './taskPrioritySelect';
import PinnedSaveButton from '../pinnedSaveButton/pinnedSaveButton';

const AddTask = () => {
  const { setTitle } = usePage();
  const { user } = useUser();
  const { push, goBack } = useNavigationStack();
  const { trackPage } = useAnalytics();
  const { t } = useTranslation();
  const { id: taskId } = useParams();
  const { task, updateTask, initialTask, clearInitialTask } = useTask();
  const {
    handleAddTask,
    handleEditTask,
    isEmailTagLoading,
    taskFieldError,
    clearTaskFieldError,
    isTaskError,
    taskErrorMessage,
    closeTaskErrorModal,
  } = useTaskMutations({ taskId, handleBack: () => handleBack() });
  const { errorFieldRef, matterErrorMessage } = useTaskFieldError({
    taskFieldError,
    clearTaskFieldError,
    matterId: task?.matter?.id,
  });

  const [durationHours, setDurationHours] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('');
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [onDiscardConfirm, setOnDiscardConfirm] = useState<(() => void) | null>(null);
  const { data: taskDetails, isLoading: isTaskDetailLoading } = useFetchTaskDetail({
    id: !task ? (taskId ?? '') : '',
    relations: taskRelations,
    fields: taskFields,
  });
  const { data: statusList } = useFetchActivityStatus({ pageSize: 50 });

  useEffect(() => {
    trackPage(AnalyticsPage.TASK_ADD_EDIT);
  }, [trackPage]);
  const defaultStatus = statusList.find(
    (status) => status.id === SystemConstants.TASK_STATUS_NOT_STARTED,
  );
  const onBack = () => {
    promptDiscardIfFormChanged(() => {
      handleBack();
    });
  };
  const handleBack = () => {
    updateTask(null);
    clearInitialTask();
    goBack();
  };
  const handleTaskStatuses = () => {
    push(ROUTES.TASK_STATUSES, { returnFocusTo: 'status' });
  };
  const handleSelectMatter = () => {
    push(ROUTES.TASK_MATTER_SELECTION, { returnFocusTo: 'matter' });
  };
  const handleSelectPriority = () => {
    push(ROUTES.TASK_PRIORITY_SELECTION, { returnFocusTo: 'priority' });
  };
  const validateFields = () => {
    if (
      !task?.currentSubject ||
      !task?.dueTime ||
      !task?.startTime ||
      !task?.statusId ||
      !task?.priority
    ) {
      return true;
    }

    if (task?.isAlertFlag && !task?.alertTime) {
      return true;
    }

    return false;
  };

  const handleTaskSave = () => {
    if (validateFields()) {
      showToast('info', t('common:fillOutAllRequiredFields'));
      return;
    }
    if (task?.id) {
      handleEditTask(buildChangedTaskPayload());
    } else {
      handleAddTask(generateBasePayload());
    }
  };
  const generateBasePayload = (): IUpdateTask => {
    return {
      version: task?.version ?? '',
      completedTime: task?.completedTime,
      subject: task?.currentSubject,
      statusId: task?.statusId,
      description: task?.description,
      startTime: dayjs(task?.startTime).format('YYYY-MM-DD'),
      dueTime: dayjs(task?.dueTime).format('YYYY-MM-DD'),
      alertTime: task?.isAlertFlag ? task?.alertTime : null,
      priority: task?.priority,
      matterId: task?.matterId ?? null,
      responsibleUserId: user?.id,
      activityTypeId: !taskId ? SystemConstants.TASK_ID : undefined,
      duration: task?.duration ? Number(task.duration ?? 0) : null,
    };
  };
  // Function to prompt discard modal if form changed, or proceed directly
  const promptDiscardIfFormChanged = (onProceed: () => void) => {
    const isFormChanged = isTaskFormChanged(task, initialTask, !!taskId);
    setShowDiscardDialog(isFormChanged);
    setOnDiscardConfirm(() => onProceed);
    if (!isFormChanged) {
      onProceed?.();
    }
  };
  const handleDiscardConfirm = () => {
    onDiscardConfirm?.();
    setShowDiscardDialog(false);
    setOnDiscardConfirm(null);
  };

  const handleDiscardCancel = () => {
    setShowDiscardDialog(false);
    setOnDiscardConfirm(null);
  };
  const buildChangedTaskPayload = (): IUpdateTask => {
    const changedPayload = getChangedFields(
      initialTask as unknown as IUpdateTask,
      generateBasePayload(),
    );
    return {
      ...changedPayload,
      version: initialTask?.version ?? '0',
    } as IUpdateTask;
  };
  useEffect(() => {
    if (!isTaskDetailLoading) {
      setTitle(
        <AddEditTransactionHeader
          handleBack={onBack}
          showCross
          validateOnFormChanged={promptDiscardIfFormChanged}
          title={!task?.id ? t('task:addNew') : (task?.subject ?? '')}
          subTitle={!task?.id ? '' : t('task:edit')}
        />,
      );
    }
  }, [task, taskId, location?.pathname, isTaskDetailLoading]);
  useEffect(() => {
    if (taskDetails) {
      updateTask({
        ...taskDetails,
        currentSubject: taskDetails?.subject,
        isAlertFlag: !!taskDetails?.alertTime,
      });
    }
    if (!taskId) {
      updateTask({
        ...task,
        priority: task?.priority ?? TasksPriority.normal,
        status: task?.status ?? defaultStatus,
        statusId: task?.status?.id ?? defaultStatus?.id,
      } as unknown as ITask);
    }
  }, [taskDetails, location?.pathname, defaultStatus]);
  useEffect(() => {
    if (task?.duration) {
      const hours = Math.floor(task.duration);
      const minutes = Math.round((task.duration - hours) * 60);
      setDurationHours(hours.toString());
      setDurationMinutes(minutes.toString());
    } else {
      setDurationHours('');
      setDurationMinutes('');
    }
  }, [task?.duration]);
  const handleDurationBlur = () => {
    if (durationHours === '' && durationMinutes === '') {
      updateTask({ ...task, duration: undefined } as unknown as ITask);
      return;
    }
    const h = durationHours === '' ? 0 : Number.parseInt(durationHours);
    const m = durationMinutes === '' ? 0 : Math.min(Number.parseInt(durationMinutes), 59);
    const newDuration = h + m / 60;
    const rounded = Math.round(newDuration * 100) / 100;
    updateTask({ ...task, duration: rounded === 0 ? undefined : rounded } as unknown as ITask);
  };

  if (isTaskDetailLoading || isEmailTagLoading) return <Spin fullscreen />;
  return (
    <div className="h-full w-full pb-gutter add-transaction-form">
      <FormCard>
        <Form layout="vertical" className="w-full d-flex fd-column gap-gutter">
          <div className="relative">
            <FormItem label={t('common:subject')} required>
              <TextAreaField
                data-testid="task-subject"
                maxLength={500}
                value={task?.currentSubject ?? ''}
                placeholder={t('common:addSubject')}
                onChange={(e) => {
                  updateTask({
                    ...task,
                    currentSubject: e?.target?.value ?? '',
                  } as ITask);
                }}
              />
            </FormItem>
          </div>
          <FormItem label={t('task:description')}>
            <TextAreaField
              data-testid="task-description"
              value={task?.description ?? ''}
              onChange={(e) => {
                updateTask({
                  ...task,
                  description: e.target.value,
                } as ITask);
              }}
              maxLength={1000}
              placeholder={t('common:addDescription')}
            />
          </FormItem>
          <Flex className="w-full gap-gutter">
            <FormItem className="w-full" label={t('task:startDate')} required={true}>
              <CustomDatePicker
                data-testid="task-start-date"
                className="w-full"
                placeholder={t('transaction:date')} // Placeholder text
                value={task?.startTime ? dayjs(task?.startTime) : undefined} // Convert value to dayjs object
                onChange={(date) => {
                  const value = date ? date.format() : undefined; // Convert date to ISO string
                  let updatedDueTime = task?.dueTime;
                  if (updatedDueTime && value && new Date(value) > new Date(updatedDueTime)) {
                    updatedDueTime = value;
                  }
                  updateTask({
                    ...task,
                    startTime: value,
                    dueTime: updatedDueTime,
                  } as ITask);
                }}
                allowClear={false}
                suffixIcon={<CalendarOutline className="calendar-icon" />}
                placement="bottomLeft"
                showWeek
                superNextIcon={null}
                superPrevIcon={null}
              />
            </FormItem>
            <FormItem className="w-full" label={t('task:dueDate')} required={true}>
              <CustomDatePicker
                data-testid="task-due-date"
                className="w-full"
                placeholder={t('task:dateInputPlaceholder')}
                value={task?.dueTime ? dayjs(task?.dueTime) : undefined}
                disabledDate={(current) => {
                  return task?.startTime ? current?.isBefore(dayjs(task?.startTime), 'day') : false;
                }}
                onChange={(date) => {
                  const value = date ? date.format() : undefined;
                  updateTask({
                    ...task,
                    dueTime: value,
                  } as ITask);
                }}
                allowClear={false}
                suffixIcon={<CalendarOutline className="calendar-icon" />}
                placement="bottomLeft"
                showWeek
                superNextIcon={null}
                superPrevIcon={null}
              />
            </FormItem>
          </Flex>
          <div className="d-flex justify-between items-center w-full billable-switch">
            <Heading
              data-testid="task-alert-heading"
              level={6}
              $color="title"
              $fontsize="13px"
              $weight={400}
              $lineheight="20px"
            >
              {t('task:alert')}
            </Heading>
            <Switch
              data-testid="task-alert-switch"
              aria-label={t('task:alert')}
              value={task?.isAlertFlag ?? false}
              onChange={(e) => {
                updateTask({
                  ...task,
                  isAlertFlag: e,
                } as ITask);
              }}
            />
          </div>

          {task?.isAlertFlag && (
            <Flex className="w-full gap-gutter">
              <FormItem className="w-full" label={t('task:alertDate')} required={true}>
                <CustomDatePicker
                  data-testid="task-alert-date"
                  className="w-full"
                  placeholder={t('task:dateInputPlaceholder')} // Placeholder text
                  value={task?.alertTime ? dayjs(extractDate(task?.alertTime)) : undefined} // Convert value to dayjs object
                  onChange={(date) => {
                    const newAlertTime = mergeDateAndTime(
                      date?.toDate(), // Convert dayjs object to Date
                      extractTime(task?.alertTime)!,
                    );
                    updateTask({ ...task, alertTime: newAlertTime.toISOString() } as ITask);
                  }}
                  allowClear={false}
                  suffixIcon={<CalendarOutline className="calendar-icon" />}
                  placement="bottomLeft"
                  showWeek
                  superNextIcon={null}
                  superPrevIcon={null}
                />
              </FormItem>
              <FormItem required={true} className="w-full" label={t('task:alertTime')}>
                <TimePicker
                  data-testid="task-alert-time"
                  className="w-full"
                  format="HH:mm"
                  placeholder={t('task:timeInputPlaceholder')} // Placeholder text
                  value={task?.alertTime ? dayjs(extractTime(task?.alertTime)) : undefined} // Convert value to dayjs object
                  onChange={(time) => {
                    const newAlertTime = mergeDateAndTime(
                      extractDate(task?.alertTime)!,
                      time?.toDate() as Date, // Convert dayjs object to Date
                    );
                    updateTask({ ...task, alertTime: newAlertTime.toISOString() } as ITask);
                  }}
                  allowClear={false}
                  placement="bottomLeft"
                  classNames={{ popup: { root: 'custom-time-picker' } }}
                  getPopupContainer={(trigger) =>
                    (trigger.parentNode as HTMLElement) || document.body
                  }
                  minuteStep={1} // Equivalent to `interval={1}`
                  showNow={false}
                />
              </FormItem>
            </Flex>
          )}
          <FormItem label={t('task:duration')} className="w-full">
            <Flex gap={5}>
              <Flex vertical gap={5}>
                <Input
                  data-testid="duration-hours"
                  className="task-duration-input"
                  placeholder="00"
                  value={durationHours}
                  onBlur={handleDurationBlur}
                  onChange={(e) => {
                    const hours = e.target.value;
                    if (hours === '' || /^\d{1,3}$/.test(hours)) {
                      setDurationHours(hours);
                    }
                  }}
                  size="large"
                />
                <Text $color="description" $fontsize="11px" $lineheight="15px" $fontstyle="italic">
                  {t('common:hours').charAt(0).toUpperCase() + t('common:hours').slice(1)}
                </Text>
              </Flex>
              <Text
                $fontsize="13px"
                $color="description"
                $lineheight="40px"
                className="d-flex items-center h-full"
              >
                :
              </Text>
              <Flex vertical gap={5}>
                <Input
                  data-testid="duration-minutes"
                  className="task-duration-input"
                  placeholder="00"
                  value={durationMinutes}
                  onBlur={handleDurationBlur}
                  onChange={(e) => {
                    const minutes = e.target.value;
                    if (minutes === '' || /^\d{1,2}$/.test(minutes)) {
                      const m =
                        minutes === '' ? '' : Math.min(Number.parseInt(minutes), 59).toString();
                      setDurationMinutes(m);
                    }
                  }}
                  size="large"
                />
                <Text $color="description" $fontsize="11px" $lineheight="15px" $fontstyle="italic">
                  {t('common:minutes').charAt(0).toUpperCase() + t('common:minutes').slice(1)}
                </Text>
              </Flex>
            </Flex>
          </FormItem>

          <FormItem label={t('task:status')} required={true}>
            <div className="relative">
              <TaskStatusSelect onOpenPicker={handleTaskStatuses} />
            </div>
          </FormItem>

          <FormItem label={t('task:priority')} name="priority" required={true}>
            <div className="relative">
              <TaskPrioritySelect onOpenPicker={handleSelectPriority} />
            </div>
          </FormItem>
          <FormItem
            data-testid="task-matter-form-item"
            label={t('matter:matter')}
            name="matter"
            validateStatus={matterErrorMessage ? 'error' : undefined}
            className={task?.disableMatterChange ? 'disabled' : undefined}
            extra={
              matterErrorMessage ? (
                <FieldErrorMessage ref={errorFieldRef} text={matterErrorMessage} />
              ) : null
            }
          >
            <div className="relative">
              <TaskMatterSelect
                disabled={task?.disableMatterChange}
                onOpenPicker={handleSelectMatter}
                onClear={() =>
                  updateTask({
                    ...task,
                    matter: undefined,
                    matterId: undefined,
                  } as unknown as ITask)
                }
              />
            </div>
          </FormItem>

          <PinnedSaveButton
            disabled={validateFields()}
            onClick={handleTaskSave}
            data-testid="task-save-button"
          >
            {taskId ? t('common:saveChanges') : t('task:addNew')}
          </PinnedSaveButton>
        </Form>
      </FormCard>
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
      <ApiErrorModal
        data-testid="task-error-modal"
        visible={isTaskError}
        message={taskErrorMessage}
        onClose={closeTaskErrorModal}
      />
    </div>
  );
};

export default AddTask;
